import { BulkApprovalBar } from "../BulkApprovalBar";
import { TruncatedTableValue } from "components/DesignSystem/tableColumnSizing";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import * as Mui from "@mui/material";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { customGetAPI } from "services/utils/CustomAPI";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { StatusRow } from "components/CustomComponents/StatusRow";
import ApprovalAuditTrail from "components/DesignSystem/ApprovalAuditTrail";
import {
  scrollTop,
  getStatusFlow,
  StatusBadge,
  displayFlexItem,
} from "services/constants";
import { Apply } from "./Apply";
import { useSelector } from "react-redux";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { Box } from "@mui/material";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { showToast } from "services/utils/Status";
import { TeamZoneOnDuty } from "../Headers";
import { OnDutyDetailPanel } from "./OnDutyDetailPanel";
import { useSavedHeaderViews } from "components/CustomComponents/useSavedHeaderViews";
import { SavedViewSelector } from "components/CustomComponents/SavedViewSelector";
import {
  OnDutyStatusCards,
  canonicalOnDutyStatus,
  onDutyStatusVisual,
} from "./OnDutyStatusCards";
import { useExpandablePanel } from "services/hooks/useExpandablePanel";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  designType,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  bulkSelectionCheckboxSx,
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableHeadCell,
  approveTableBodyCell,
  NewRequestButton,
  ApproveTable,
} from "../approveUi";
import moment from "moment";
import { resolveRangeLabel } from "../dateRangeUtils";
import { normalizeApprovalPersonFields } from "../displayNameUtils";

// Same "Default View" sentinel pattern as Leave/index.js and
// Permission/index.js.
const ONDUTY_LIST_DEFAULT_VIEW_ID = "__default_onduty_list_view__";

// Same date-format tolerance WorkAdjustment/index.js's own toISODate uses —
// the actual-special-attendance API's `date` field isn't guaranteed to
// already be ISO, so this normalizes DD-MM-YYYY-ish strings before resend.
const toISODate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split("-");
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return dateStr;
};

export const OnDuty = ({
  setReCallApi,
  reCallApi,
  startDate,
  endDate,
  dateRangeLabel: dateRangeLabelProp,
  handleStartDate,
  subModuleId,
}) => {
  const { domain, token, userDetails, topbarCompanyId, topbarCompanyName, userScreenAccess } =
    useSelector((state) => state.tokenReducer);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const containerRef = useRef(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const [isCreatePanelOpen, setIsCreatePanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [onDutyData, setOnDutyData] = useState([]);
  const [onDutyFilterData, setOnDutyFilterData] = useState([]);
  const [viewOnDutyDetails, setViewOnDutyDetails] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [expandedData, setExpandedData] = useState(null);
  const [statusCardFilter, setStatusCardFilter] = useState("");
  // Initialized from (and kept in sync with) the Approval Center landing
  // page's own dateRangeLabel prop — previously hardcoded to "Last 7 Days"
  // regardless of the actual startDate/endDate this screen was handed, on
  // a fresh ?view= deep link or Team Workspace Home's "date" param. A
  // manual re-pick on this screen's own picker still overrides it locally.
  const [dateRangeLabel, setDateRangeLabel] = useState(
    dateRangeLabelProp || "Last 7 Days",
  );

  useEffect(() => {
    if (dateRangeLabelProp) setDateRangeLabel(dateRangeLabelProp);
  }, [dateRangeLabelProp]);
  const [selectedRowKeys, setSelectedRowKeys] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  const {
    expanded: onDutyPanelExpanded,
    toggleExpanded: toggleOnDutyPanelExpanded,
  } = useExpandablePanel(viewOnDutyDetails);
  const {
    expanded: createOnDutyPanelExpanded,
    toggleExpanded: toggleCreateOnDutyPanelExpanded,
  } = useExpandablePanel(isCreatePanelOpen);

  useEffect(() => {
    const fetchOnDutyDetails = async () => {
      const params = new URLSearchParams({
        user_id: String(userDetails?.id ?? ""),
        company_id: String(topbarCompanyId ?? ""),
        start_date: String(startDate ?? ""),
        end_date: String(endDate ?? ""),
        attendance_type: "On Duty",
      });
      const queryString = params.toString().replace(/\+/g, "%20");
      const res = await customGetAPI(
        `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?${queryString}&company_id=${topbarCompanyId}`,
      );
      const rows = Array.isArray(res?.responseDetails)
        ? res.responseDetails
        : (res?.responseDetails?.results ?? []);
      const mappedRows = rows
        .filter((row) => row?.attendance_type === "On Duty")
        .map((row) => ({
          ...row,
          // getStatusFlow (services/constants) already reads these same
          // fields for the expand row's approval timeline — one column
          // showing whichever of the three actually fired for this row.
          approver_name:
            row.approved_by || row.rejected_by || row.cancelled_by || "-",
        }))
        .map(normalizeApprovalPersonFields);
      setOnDutyData(mappedRows);
      // Also seed the filtered view directly — CustomFilter recomputes it
      // in its own effect a render later, so without this the table
      // flashes "No Matching Results" for a frame right as loading ends.
      setOnDutyFilterData(mappedRows);
      setLoading(false);
      setReCallApi(false);
    };
    if (startDate && endDate && reCallApi) {
      setLoading(true);
      fetchOnDutyDetails();
    }
  }, [
    domain,
    endDate,
    reCallApi,
    setReCallApi,
    startDate,
    topbarCompanyId,
    userDetails?.id,
  ]);

  // Checks its own "On Duty" submodule rather than sharing WorkAdjustment's
  // bucket — the backend doesn't provision this submodule yet, so this
  // reads as no access until it's added there.
  let check_access = checkEditorPermission(
    userScreenAccess?.module_details,
    "Approvals",
    "On Duty",
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    scrollTop(containerRef);
  }, [page]);

  useEffect(() => {
    if (!footerNode) return;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  useEffect(() => {
    if (!customFilterNode) return;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode]);

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setOnDutyFilterData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  // Tallies for the Pending/Approved/Rejected cards using current filters.
  const onDutyStatusCounts = useMemo(
    () =>
      (onDutyFilterData || []).reduce((acc, request) => {
        const status = canonicalOnDutyStatus(request?.status);
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {}),
    [onDutyFilterData],
  );

  const visibleOnDutyData = useMemo(() => {
    if (!statusCardFilter) return onDutyFilterData;
    return onDutyFilterData.filter(
      (request) => canonicalOnDutyStatus(request?.status) === statusCardFilter,
    );
  }, [onDutyFilterData, statusCardFilter]);

  const handleStatusCardClick = (key) => {
    clearSelection();
    setShowBulkRejectDialog(false);
    setPage(0);
    setExpandedIndex(null);
    setExpandedData(null);
    setViewOnDutyDetails(false);
    setSelectedData(null);
    if (key === "All") {
      setStatusCardFilter("");
    } else {
      setStatusCardFilter(key);
    }
  };

  // Bulk select/approve/reject — mirrors the multi-select action bar the
  // old combined WorkAdjustment screen has always had, which this split
  // otherwise missed carrying over. Selection is scoped to the rows
  // currently visible in the table (post status-card + CustomFilter
  // filtering) so "select all" always matches what's on screen.
  const isSelected = (row) => selectedRowKeys.has(row.id);

  const pendingRows = useMemo(
    () => visibleOnDutyData.filter((row) => row.status === "Pending"),
    [visibleOnDutyData],
  );

  const toggleRow = (row) => {
    if (row.status !== "Pending") return;
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      next.has(row.id) ? next.delete(row.id) : next.add(row.id);
      return next;
    });
  };

  const toggleAll = () => {
    const allPendingSelected =
      pendingRows.length > 0 && pendingRows.every((r) => isSelected(r));
    if (allPendingSelected) {
      setSelectedRowKeys(new Set());
    } else {
      setSelectedRowKeys(new Set(pendingRows.map((r) => r.id)));
    }
  };

  const isAllSelected =
    pendingRows.length > 0 && pendingRows.every((r) => isSelected(r));
  const isIndeterminate =
    selectedRowKeys.size > 0 && selectedRowKeys.size < pendingRows.length;

  const clearSelection = () => {
    setSelectedRowKeys(new Set());
    setBulkRejectReason("");
  };

  const selectedRows = visibleOnDutyData.filter((r) => isSelected(r));

  const buildBulkPayload = (row, status, reason) => ({
    id: row.id,
    user_id: row.user_id,
    attendance_type: "On Duty",
    reason: row.reason,
    status,
    date: toISODate(row.date),
    start_time: row.start_time ?? null,
    end_time: row.end_time ?? null,
    ...(status === "Rejected" && { rejected_reason: reason || "" }),
  });

  // Sends a raw JSON array body — customAPI spreads its payload argument
  // ({...payload}), which would turn an array into a numeric-keyed object,
  // so bulk update goes straight to fetch instead (same reason the old
  // combined WorkAdjustment screen's bulk action bar does this).
  const callBulkAPI = async (status, reason) => {
    const payload = selectedRows.map((row) =>
      buildBulkPayload(row, status, reason),
    );
    try {
      const response = await fetch(
        `${domain}team_zone/approve/work_adjustment/actual-special-attendance/?company_id=${topbarCompanyId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );
      const res = await response.json();
      if (response.ok) {
        return { ok: true, code: response.status, responseDetails: res };
      }
      return {
        ok: false,
        code: response.status,
        message:
          res?.error ||
          res?.errors?.[0]?.message ||
          res?.message ||
          "Bulk update failed",
      };
    } catch (error) {
      return { ok: false, message: error.message };
    }
  };

  const handleBulkApprove = async () => {
    if (!selectedRows.length) return;
    setBulkLoading(true);
    const response = await callBulkAPI("Approved");
    if (response.ok) {
      showToast("success", "Records approved.");
      clearSelection();
      setReCallApi(true);
    } else {
      showToast("error", response?.message || "Failed to approve");
    }
    setBulkLoading(false);
  };

  const handleBulkReject = async () => {
    if (!selectedRows.length) return;
    setBulkLoading(true);
    const response = await callBulkAPI("Rejected", bulkRejectReason);
    if (response.ok) {
      showToast("success", "Records rejected.");
      clearSelection();
      setShowBulkRejectDialog(false);
      setReCallApi(true);
    } else {
      showToast("error", response?.message || "Failed to reject");
    }
    setBulkLoading(false);
  };

  const handleOnDutyDateRange = (rangeStart, rangeEnd, rangeLabel) => {
    handleStartDate(rangeStart, rangeEnd);
    setDateRangeLabel(resolveRangeLabel(rangeLabel));
  };

  const handleCreateClosePanel = () => {
    setIsCreatePanelOpen(false);
  };

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const onDutyColumnHeaders = useMemo(
    () => TeamZoneOnDuty.filter((header) => header.key !== "expand"),
    [],
  );

  // Default View / saved-view selector — same shape as Leave/index.js and
  // Permission/index.js. Its own storageKey namespaces this screen's saved
  // views separately from WorkAdjustment's and Permission's, even though all
  // three share the same subModuleId.
  const {
    savedViewsForScope,
    activeViewId,
    selectView,
    selectDefaultView,
    deleteView,
    createView,
    upsertView,
    updateView,
  } = useSavedHeaderViews({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "team_onduty_saved_views",
    scope: "default",
    onViewChange: handleSavedViewChange,
    defaultViewId: ONDUTY_LIST_DEFAULT_VIEW_ID,
  });
  const persistedDefaultViewId = activeViewId || ONDUTY_LIST_DEFAULT_VIEW_ID;
  const [currentViewId, setCurrentViewId] = useState(null);

  useEffect(() => {
    setCurrentViewId((prev) => {
      if (prev && prev !== ONDUTY_LIST_DEFAULT_VIEW_ID) {
        return prev;
      }
      return persistedDefaultViewId;
    });
  }, [persistedDefaultViewId]);

  const baseDefaultHeaders = useMemo(
    () => onDutyColumnHeaders,
    [onDutyColumnHeaders],
  );

  const storedDefaultView = useMemo(
    () =>
      savedViewsForScope.find(
        (view) => view.id === ONDUTY_LIST_DEFAULT_VIEW_ID,
      ) || null,
    [savedViewsForScope],
  );

  const customSavedViews = useMemo(
    () =>
      savedViewsForScope.filter(
        (view) => view.id !== ONDUTY_LIST_DEFAULT_VIEW_ID,
      ),
    [savedViewsForScope],
  );

  const defaultView = useMemo(() => {
    if (storedDefaultView?.isCustomDefault) {
      // Whatever headerConfig the user saved is authoritative — same as any
      // other saved view. (Previously this back-filled any header missing
      // from headerConfig, meant to surface columns added to the schema
      // after the view was saved — but that's indistinguishable from a
      // column the user deliberately unchecked, so it silently re-added
      // every unchecked header right back on the next render.)
      return storedDefaultView;
    }

    return {
      id: ONDUTY_LIST_DEFAULT_VIEW_ID,
      name: "Default View",
      headerConfig: baseDefaultHeaders.map((header) => ({
        key: header.key,
        name: header.name,
      })),
      headerKeys: baseDefaultHeaders.map((header) => header.key),
      isDefaultView: true,
    };
  }, [baseDefaultHeaders, storedDefaultView]);

  const effectiveViewId = currentViewId ?? persistedDefaultViewId;

  const activeView = useMemo(() => {
    if (effectiveViewId === ONDUTY_LIST_DEFAULT_VIEW_ID) {
      return defaultView;
    }

    return (
      customSavedViews.find((view) => view.id === effectiveViewId) ||
      defaultView
    );
  }, [customSavedViews, defaultView, effectiveViewId]);

  const visibleHeaders = useMemo(() => {
    const headerByKey = new Map(
      onDutyColumnHeaders.map((header) => [header.key, header]),
    );

    if (activeView?.headerConfig?.length) {
      const orderedHeaders = activeView.headerConfig
        .map((entry) => {
          const baseHeader = headerByKey.get(entry.key);
          if (!baseHeader) return null;
          return {
            ...baseHeader,
            name: entry.name?.trim() || baseHeader.name,
          };
        })
        .filter(Boolean);

      return orderedHeaders.length ? orderedHeaders : baseDefaultHeaders;
    }

    if (activeView?.headerKeys?.length) {
      const selectedKeys = new Set(activeView.headerKeys);
      const filteredHeaders = onDutyColumnHeaders.filter((header) =>
        selectedKeys.has(header.key),
      );
      return filteredHeaders.length ? filteredHeaders : baseDefaultHeaders;
    }

    return baseDefaultHeaders;
  }, [activeView, baseDefaultHeaders, onDutyColumnHeaders]);

  const handleSelectView = useCallback(
    (viewId) => {
      setCurrentViewId(viewId);
      handleSavedViewChange();
    },
    [handleSavedViewChange],
  );

  const handleSelectDefaultView = useCallback(() => {
    setCurrentViewId(ONDUTY_LIST_DEFAULT_VIEW_ID);
    handleSavedViewChange();
  }, [handleSavedViewChange]);

  const handleMakeDefaultView = useCallback(
    (viewId) => {
      if (viewId === ONDUTY_LIST_DEFAULT_VIEW_ID) {
        selectDefaultView();
        setCurrentViewId(ONDUTY_LIST_DEFAULT_VIEW_ID);
        return;
      }

      selectView(viewId);
      setCurrentViewId(viewId);
    },
    [selectDefaultView, selectView],
  );

  const handleCreateView = useCallback(
    (viewName, headerConfig) => {
      createView(viewName, headerConfig, { activate: false });
    },
    [createView],
  );

  const handleDeleteView = useCallback(
    (viewId) => {
      if (currentViewId === viewId) {
        setCurrentViewId(ONDUTY_LIST_DEFAULT_VIEW_ID);
      }
      deleteView(viewId);
    },
    [currentViewId, deleteView],
  );

  const handleEditDefaultView = useCallback(
    (viewId, viewName, headerConfig) => {
      upsertView(
        viewId || ONDUTY_LIST_DEFAULT_VIEW_ID,
        viewName || "Default View",
        headerConfig,
        "Default view updated successfully.",
        { isCustomDefault: true },
      );
      setCurrentViewId(ONDUTY_LIST_DEFAULT_VIEW_ID);
    },
    [upsertView],
  );

  const titleOverride = useMemo(
    () => (
      <SavedViewSelector
        headers={onDutyColumnHeaders}
        defaultHeaders={baseDefaultHeaders}
        defaultView={defaultView}
        savedViews={customSavedViews}
        activeView={activeView}
        activeViewId={effectiveViewId}
        persistedDefaultViewId={persistedDefaultViewId}
        screenAccess={check_access}
        onSelectDefaultView={handleSelectDefaultView}
        onSelectView={handleSelectView}
        onCreateView={handleCreateView}
        onUpdateView={(viewId, viewName, headerConfig) => {
          if (viewId === ONDUTY_LIST_DEFAULT_VIEW_ID) {
            handleEditDefaultView(viewId, viewName, headerConfig);
            return;
          }
          updateView(viewId, viewName, headerConfig);
        }}
        onDeleteView={handleDeleteView}
        onMakeDefaultView={handleMakeDefaultView}
      />
    ),
    [
      activeView,
      baseDefaultHeaders,
      check_access,
      customSavedViews,
      defaultView,
      effectiveViewId,
      handleCreateView,
      handleDeleteView,
      handleEditDefaultView,
      handleMakeDefaultView,
      handleSelectDefaultView,
      handleSelectView,
      onDutyColumnHeaders,
      persistedDefaultViewId,
      updateView,
    ],
  );

  const reportMetadata = useMemo(
    () => ({
      "Company Name": topbarCompanyName || "N/A",
      "Date Range": `${moment(startDate).format("DD/MM/YYYY")} to ${moment(endDate).format("DD/MM/YYYY")}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [endDate, startDate, topbarCompanyName],
  );

  const newOnDutyButton = (
    <NewRequestButton
      hasAccess={check_access}
      deniedMessage="Access denied: You are not authorized to create an on duty request"
      onClick={() => setIsCreatePanelOpen(true)}
    />
  );

  return loading ? (
    <Loader />
  ) : (
    <React.Fragment>
      <Box sx={{ flexGrow: 1, width: "100%", overflow: "visible" }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Mui.Typography sx={designType.pageTitle}>On Duty</Mui.Typography>
            <Mui.Typography sx={designType.pageSubtitle}>
              Review and manage On Duty requests awaiting approval.
            </Mui.Typography>
          </Box>

          <Mui.Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <ApprovalDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              onChange={handleOnDutyDateRange}
              includeCurrentDate
              showToday
            />
            {newOnDutyButton}
          </Mui.Stack>
        </Box>

        <OnDutyStatusCards
          counts={onDutyStatusCounts}
          activeFilter={statusCardFilter}
          onCardClick={handleStatusCardClick}
        />

        <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
          <CustomFilter
            title={"On Duty Requests"}
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={onDutyData}
            filteredData={visibleOnDutyData}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={check_access}
            hideCount={true}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
             labeledToolbar={true}
          />
        </Box>
        <BulkApprovalBar
          selectedCount={statusCardFilter === "Pending" ? selectedRowKeys.size : 0}
          loading={bulkLoading}
          onReject={() => setShowBulkRejectDialog(true)}
          onApprove={handleBulkApprove}
        />
        <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
          <Mui.TableContainer
            ref={containerRef}
            sx={{
              width: "100%",
              overflowX: { xs: "auto", sm: "visible" },
              overflowY: { xs: "auto", sm: "visible" },
              pb: footerHeight ? `${footerHeight}px` : 0,
            }}
          >
            <ApproveTable
              stickyHeader
              sx={{
                minWidth: 920,
                width: "100%",
                tableLayout: "fixed",
              }}
            >
              <Mui.TableHead sx={designShell.tableStickyHead}>
                <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                  {statusCardFilter === "Pending" && (
                    <Mui.TableCell
                      padding="checkbox"
                      sx={{
                        ...approveTableHeadCell,
                        top: APP_BAR_OFFSET + customFilterHeight,
                      }}
                    >
                      {statusCardFilter === "Pending" && pendingRows.length > 0 && (
                        <Mui.Checkbox
                          size="small"
                          sx={bulkSelectionCheckboxSx}
                          checked={isAllSelected}
                          indeterminate={isIndeterminate}
                          onChange={toggleAll}
                        />
                      )}
                    </Mui.TableCell>
                  )}
                  <Mui.TableCell
                    sx={{
                      ...approveTableHeadCell,
                      top: APP_BAR_OFFSET + customFilterHeight,
                      width: 40,
                    }}
                  />
                  {visibleHeaders?.map((value) => (
                    <Mui.TableCell
                      key={value.key}
                      sx={{
                        ...approveTableHeadCell,
                        top: APP_BAR_OFFSET + customFilterHeight,
                      }}
                    >
                      <TruncatedTableValue value={value?.name} />
                    </Mui.TableCell>
                  ))}
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {visibleOnDutyData?.length === 0 ? (
                  <TableEmptyState
                    dataLength={onDutyData}
                    colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                  />
                ) : (
                  visibleOnDutyData
                    ?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    ?.map((value, index) => (
                      <React.Fragment key={value?.id}>
                        <Mui.TableRow
                          onClick={() => {
                            setSelectedData(value);
                            setViewOnDutyDetails(true);
                          }}
                          sx={{
                            cursor: "pointer",
                            ...designShell.tableRowHover,
                            ...(viewOnDutyDetails &&
                            value?.id === selectedData?.id
                              ? designShell.neutralSelectedRow
                              : {}),
                            "&:last-child td": { borderBottom: "none" },
                          }}
                        >
                          {statusCardFilter === "Pending" && (
                            <Mui.TableCell
                              padding="checkbox"
                              sx={approveTableBodyCell}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {statusCardFilter === "Pending" && value.status === "Pending" && (
                                <Mui.Checkbox
                                  size="small"
                                  sx={bulkSelectionCheckboxSx}
                                  checked={isSelected(value)}
                                  onChange={() => toggleRow(value)}
                                />
                              )}
                            </Mui.TableCell>
                          )}
                          <Mui.TableCell
                            sx={approveTableBodyCell}
                            onClick={(event) => {
                              event.stopPropagation();
                              setExpandedIndex((prev) =>
                                prev === index ? null : index,
                              );
                              setExpandedData(value);
                            }}
                          >
                            <ArrowRightIcon
                              sx={{
                                color: designColors.slate500,
                                cursor: "pointer",
                                transition: "transform 0.2s ease",
                                transform:
                                  expandedIndex === index
                                    ? "rotate(90deg)"
                                    : "rotate(0deg)",
                              }}
                            />
                          </Mui.TableCell>
                          {visibleHeaders.map((head) => (
                            <Mui.TableCell key={head.key} data-label={head.name} sx={approveTableBodyCell}>
                              {head.key === "employee_name" ? (
                                <Mui.Stack direction="row" spacing={1} alignItems="center">
                                  <Mui.Avatar
                                    sx={{ width: 28, height: 28 }}
                                    alt={value?.employee_name?.charAt(0).toUpperCase()}
                                    src={value?.image_url}
                                  />
                                  <Mui.Typography
                                    sx={{
                                      fontWeight:
                                        viewOnDutyDetails &&
                                        value?.id === selectedData?.id
                                          ? 700
                                          : 500,
                                      color:
                                        viewOnDutyDetails &&
                                        value?.id === selectedData?.id
                                          ? designColors.slate900
                                          : "inherit",
                                      fontSize: 14,
                                    }}
                                  >
                                    {value?.employee_name || "N/A"}
                                  </Mui.Typography>
                                </Mui.Stack>
                              ) : head.key === "status" ? (
                                <Mui.Box sx={displayFlexItem}>
                                  <StatusBadge
                                    color={
                                      value?.color_code ||
                                      onDutyStatusVisual(value?.status).accent
                                    }
                                    label={value?.status || "N/A"}
                                  />
                                </Mui.Box>
                              ) : (
                                (<TruncatedTableValue value={value[head.key] ?? "N/A"} />)
                              )}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>

                        {expandedIndex === index && (
                          <Mui.TableRow>
                            <Mui.TableCell
                              colSpan={visibleHeaders.length + 1 + (statusCardFilter === "Pending" ? 1 : 0)}
                              sx={{
                                p: 2,
                                borderBottom: `1px solid ${designColors.slate200}`,
                              }}
                            >
                              {getStatusFlow(expandedData)[expandedData?.status]?.map(
                                (item, i, arr) => (
                                  <StatusRow
                                    key={i}
                                    status={item.status}
                                    user={item.user}
                                    date={item.date}
                                    showConnector={i !== arr.length - 1}
                                  />
                                ),
                              )}
                              <ApprovalAuditTrail record={expandedData} compact />
                            </Mui.TableCell>
                          </Mui.TableRow>
                        )}
                      </React.Fragment>
                    ))
                )}
              </Mui.TableBody>
            </ApproveTable>
          </Mui.TableContainer>
          <Box ref={footerRef} sx={approveStickyFooterBar}>
            {paginationFooter({
              page,
              rowsPerPage,
              total: visibleOnDutyData?.length || 0,
              itemLabel: "on duty requests",
              onPageChange: setPage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions,
            })}
          </Box>
        </Box>
      </Box>
      <Apply
        open={isCreatePanelOpen}
        onClose={() => {
          handleCreateClosePanel();
        }}
        setReCallApi={setReCallApi}
        expanded={createOnDutyPanelExpanded}
        onToggleExpand={toggleCreateOnDutyPanelExpanded}
      />
      <OnDutyDetailPanel
        open={viewOnDutyDetails}
        onClose={() => setViewOnDutyDetails(false)}
        onDuty={selectedData}
        checkAccess={check_access}
        setReCallApi={setReCallApi}
        expanded={onDutyPanelExpanded}
        onToggleExpand={toggleOnDutyPanelExpanded}
      />
      <GlobalConfirmDialog
        open={showBulkRejectDialog}
        title="Reject Selected Requests"
        onCancel={() => {
          setShowBulkRejectDialog(false);
          setBulkRejectReason("");
        }}
        onConfirm={handleBulkReject}
        confirmLabel="Reject"
        cancelLabel="Cancel"
        loading={bulkLoading}
        danger
        reasonValue={bulkRejectReason}
        onReasonChange={setBulkRejectReason}
        reasonPlaceholder="Enter a reason for rejection"
        reasonMinRows={2}
        maxWidth="sm"
      >
        <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
          Reason (optional) - applied to all selected On Duty requests.
        </Mui.Typography>
      </GlobalConfirmDialog>
    </React.Fragment>
  );
};
